import { useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, StatusPill, useToast } from '../../design-system';
import './PushDeliveryReport.css';

// Did the notifications we sent actually arrive?
//
// ⚠️ THIS SCREEN EXISTS BECAUSE THE PUSH PROTOCOL CANNOT ANSWER THAT. A 201
// from Google means Google accepted the message, and a handset in Doze that
// never wakes produces exactly the same 201 as one that buzzes in somebody's
// pocket. The only honest proof is the device calling home from the service
// worker, and the figure below is how many did — see
// controllers/notificationReceiptController.js.
//
// ⚠️ THE PER-DEVICE TABLE IS THE ACTIONABLE HALF, not the headline. "94%" tells
// a firm nothing they can do anything about; "Sam's Chrome on Android has taken
// 2 of 41" names the handset whose battery settings need changing, which is the
// commonest cause by a wide margin and is invisible from every other angle.

const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`);

const when = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
  : 'never');

// How good is good enough. Web Push is best-effort everywhere, so even a
// healthy firm does not sit at 100 — these thresholds are for "is something
// actually wrong", not for grading.
const health = (rate) => {
  if (rate === null || rate === undefined) return { variant: 'neutral', label: 'No data yet' };
  if (rate >= 90) return { variant: 'success', label: 'Healthy' };
  if (rate >= 70) return { variant: 'warning', label: 'Patchy' };
  return { variant: 'danger', label: 'Mostly not arriving' };
};

function PushDeliveryReport() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = (alive = { current: true }) => apiClient.get('/api/notifications/delivery')
    .then((r) => alive.current && setData(r.data))
    // Manager+ only, so an admin without the rank simply sees nothing rather
    // than an error about a report they were never offered.
    .catch(() => alive.current && setFailed(true));

  useEffect(() => {
    const alive = { current: true };
    load(alive);
    return () => { alive.current = false; };
  }, []);

  // ⚠️ THE RESULT IS REPORTED AS WHAT WAS SENT, NEVER AS SUCCESS. A retry is
  // the same operation that just failed — against a handset whose battery
  // manager is killing pushes it will fail again, identically. Saying
  // "re-sent 12" is true; saying "delivered 12" would not be, and this card
  // exists precisely because that distinction had nowhere to live.
  const resend = async () => {
    setRetrying(true);
    try {
      const { data: r } = await apiClient.post('/api/notifications/delivery/retry');
      if (!r.pushed) {
        toast.info(r.noDevices
          ? 'Nobody with an unconfirmed notification has a device registered right now'
          : 'Nothing left to re-send');
      } else {
        toast.success(
          `Re-sent ${r.pushed} to ${r.people} ${r.people === 1 ? 'person' : 'people'}`
          + (r.remaining ? ` · ${r.remaining} left for later` : '')
        );
      }
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not re-send');
    } finally {
      setRetrying(false);
    }
  };

  if (failed || !data) return null;

  // ⚠️ RECEIPTS OFF IS ITS OWN STATE, NOT 0%. Without PUBLIC_API_URL the
  // service worker is never told where to report, so every figure here would
  // read zero for a push system that is working perfectly — which is a far
  // worse lie than saying nothing.
  if (!data.receiptsConfigured) {
    return (
      <Card>
        <h3 className="pdr-h">Did they arrive?</h3>
        <p className="pdr-sub">
          Delivery tracking is switched off. Notifications are still being sent — this only means
          the app cannot tell a delivered one from a dropped one. Set <code>PUBLIC_API_URL</code> on
          the server to the address staff browsers reach it at, and devices will start reporting back.
        </p>
      </Card>
    );
  }

  const worst = data.devices.filter((d) => d.rate !== null && d.rate < 90);

  return (
    <Card>
      <h3 className="pdr-h">Did they arrive?</h3>
      <p className="pdr-sub">
        A push service accepting a notification is not the same as a phone getting it, so every
        device reports back when it does. Of the {data.pushed} push{data.pushed === 1 ? '' : 'es'}
        {' '}sent in the last {data.days} days:
      </p>

      {data.pushed === 0 ? (
        <p className="pdr-empty">
          Nothing has been pushed yet. Figures appear once staff turn on notifications and
          something is sent.
        </p>
      ) : (
        <>
          <div className="pdr-figures">
            <div className="pdr-figure">
              <span className="pdr-num">{pct(data.reachedPct)}</span>
              <span className="pdr-label">reached a device</span>
              <StatusPill {...health(data.reachedPct)} />
            </div>
            <div className="pdr-figure">
              <span className="pdr-num">{pct(data.clickedPct)}</span>
              <span className="pdr-label">were opened</span>
            </div>
          </div>

          <p className="pdr-note">
            {data.shown} shown on a screen · {data.suppressed} skipped because the app was already
            open in front of them — which is a success, not a miss.
          </p>

          {data.retryable > 0 && (
            <div className="pdr-retry">
              <div className="pdr-retry-text">
                <b>{data.retryable} never reached a device.</b>
                {' '}Worth another try if a phone was off or out of signal — it re-sends the
                alert only, and adds nothing to anyone&apos;s bell.
              </div>
              <Button variant="secondary" size="sm" loading={retrying} onClick={resend}>
                Send again
              </Button>
            </div>
          )}

          {worst.length > 0 && (
            <div className="pdr-worst">
              <div className="pdr-worst-head">Devices that are not receiving</div>
              <ul className="pdr-list">
                {worst.slice(0, 6).map((d, i) => (
                  <li key={`${d.userId}-${i}`} className="pdr-row">
                    <span className="pdr-who">
                      <b>{d.userName}</b>
                      <span className="pdr-device">{d.device}</span>
                    </span>
                    <span className="pdr-rate">
                      {d.received} of {d.accepted}
                      <span className="pdr-last">last {when(d.lastReceivedAt)}</span>
                    </span>
                  </li>
                ))}
              </ul>
              {/* The fix is almost never in this app. Saying so saves the
                  person the hour of looking for it here first. */}
              <p className="pdr-hint">
                Usually the phone’s battery saver. On Android: Settings → Apps → this app →
                Battery → <b>Unrestricted</b>, and allow notifications. Xiaomi, Oppo, Vivo and
                Realme each bury this somewhere different and are the usual culprits.
              </p>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export default PushDeliveryReport;
